/**
 * Sound effects — the original Android client's 23 mp3s, keyed by the rules engine's `Sound` names.
 * Near/far explosions follow the original: an explosion that involves you is "near".
 * Browsers only play audio after a user gesture, so `unlock()` is called on the first click/tap.
 */

const FILES: Record<string, string[]> = {
  explosionNear: ["nearexplosion1"],
  explosionFar: ["farexplosion1", "farexplosion2"],
  money: ["money1"],
  missileLaunch: ["missilelaunch1", "missilelaunch2", "missilelaunch3"],
  interceptorLaunch: ["interceptor1", "interceptor2", "interceptor3", "interceptor4"],
  construction: ["construction1"],
  interceptorMiss: ["interceptormiss1"],
  interceptorHit: ["interceptorhit1"],
  sentryGunHit: ["sentryhit1"],
  sentryGunMiss: ["sentrymiss1", "sentrymiss2", "sentrymiss3"],
  reconfig: ["reconfig1"],
  equip: ["equip1"],
  respawn: ["respawn1"],
  death: ["death1"],
  repair: ["construction1"],
  heal: ["respawn1"],
  airraid: ["airraid"],
};

let enabled = true;
let unlocked = false;
let volume = 0.6;
const pool = new Map<string, HTMLAudioElement[]>();
let lastPlayed = new Map<string, number>();

export function setSoundEnabled(on: boolean) { enabled = on; }
export function setVolume(v: number) { volume = Math.max(0, Math.min(1, v)); }

export function unlock() {
  if (unlocked) return;
  unlocked = true;
  // warm the cache so the first real effect isn't late
  for (const files of Object.values(FILES)) for (const f of files) get(f);
}

function get(file: string): HTMLAudioElement {
  let list = pool.get(file);
  if (!list) { list = []; pool.set(file, list); }
  const free = list.find((a) => a.paused || a.ended);
  if (free) return free;
  const a = new Audio(`/sfx/${file}.mp3`);
  a.preload = "auto";
  list.push(a);
  return a;
}

/** Play a rules `Sound`. `near` picks the close explosion; throttled so a salvo of 50 doesn't stack 50 plays. */
export function play(sound: string, near = false) {
  if (!enabled || !unlocked || sound === "none") return;
  const key = sound === "explosion" ? (near ? "explosionNear" : "explosionFar") : sound;
  const files = FILES[key];
  if (!files) return;
  const now = performance.now();
  if (now - (lastPlayed.get(key) ?? 0) < (key === "airraid" ? 8000 : 250)) return;
  lastPlayed.set(key, now);
  const a = get(files[Math.floor(Math.random() * files.length)]);
  a.volume = key === "explosionFar" ? volume * 0.5 : volume;
  a.currentTime = 0;
  a.play().catch(() => { /* autoplay policy — ignore */ });
}
